<template>
  <v-container
    v-if="$can('view', 'YachtModel') && yachtModel.data"
    fluid
    class="pa-0"
  >
    <v-row
      no-gutters
      justify="center"
    >
      <v-col cols="14">
        <v-row
          align="center"
          no-gutters
          class="fill-height"
        >
          <v-col
            class="px-6 py-4"
            cols="auto"
          >
            <v-avatar
              color="grey lighten-4"
              size="110px"
            >
              <img
                v-if="yachtModel.data.main_image"
                :src="yachtModel.data.main_image.path"
                alt="Main image"
              >
            </v-avatar>
          </v-col>
          <v-col class="fill-height">
            <v-row
              no-gutters
              class="flex-column fill-height"
            >
              <v-row no-gutters>
                <v-col>
                  <div class="title mb-1">
                    <strong>#{{ yachtModel.data.id }}</strong> &middot;
                    <span v-if="yachtModel.data.label">
                      {{ yachtModel.data.label }}
                    </span>
                    <span
                      v-else
                      class="grey--text text--lighten-1"
                    >
                      No Name
                    </span>
                  </div>
                  <div class="body-2 mb-1">
                    Model Year <strong>{{ yachtModel.data.model_year }}</strong> &middot;
                    <v-chip
                      class="mr-2"
                      label
                      small
                      :color="yachtModelStatusData[yachtModel.data.status].color"
                      dark
                    >
                      {{ yachtModelStatusData[yachtModel.data.status].label }}
                    </v-chip>

                    <v-chip
                      class="mr-2"
                      label
                      small
                      :color="yachtModel.data.is_sales_active ? 'success' : 'grey darken-1'"
                      dark
                    >
                      {{ yachtModel.data.is_sales_active ? "Available for sales" : "Sales unavailable" }}
                    </v-chip>

                    <v-chip
                      label
                      small
                      :color="yachtModel.data.is_partners_visible ? 'success' : 'grey darken-1'"
                      dark
                    >
                      {{ yachtModel.data.is_partners_visible ? "Available for partners" : "Unavailable for partners" }}
                    </v-chip>
                  </div>
                  <div class="grey--text text--lighten-1 caption">
                    updated {{ yachtModel.data.updated_at | moment('from') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <!-- todo: set permission-->
                  <v-tooltip
                    v-if="$can('edit', 'YachtModel')"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        class="mr-2"
                        v-on="on"
                        @click="yachtModelEditDialog = true"
                      >
                        <v-icon>
                          edit
                        </v-icon>
                      </v-btn>
                    </template>
                    Edit yacht model
                  </v-tooltip>
                </v-col>
              </v-row>
              <div class="col col-auto">
                <v-tabs background-color="transparent">
                  <v-tab
                    :to="{ name: 'yachtModelDetails' }"
                    exact
                    class="text-none font-weight-regular"
                  >
                    Details
                  </v-tab>
                  <v-tab
                    :to="{ name: 'yachtModelDetailsYardEquipment' }"
                    exact
                    class="text-none font-weight-regular"
                  >
                    Yard Equipment
                  </v-tab>
                  <v-tab
                    :to="{ name: 'yachtModelDetailsAdditionalEquipment' }"
                    exact
                    class="text-none font-weight-regular"
                  >
                    Additional Options & CDR
                  </v-tab>
                  <v-tab
                    :to="{ name: 'yachtModelDetailsFinanceModels' }"
                    exact
                    class="text-none font-weight-regular"
                  >
                    Finance
                  </v-tab>
                  <v-tab
                    exact
                    class="text-none font-weight-regular"
                  >
                    Yachts
                  </v-tab>
                </v-tabs>
              </div>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-divider />

    <v-row
      v-if="yachtModel.data"
      class="mt-6 mb-16"
      no-gutters
      justify="center"
    >
      <v-col cols="14">
        <v-fade-transition mode="out-in">
          <router-view
            :yacht-model.sync="yachtModel.data"
            @update="fetchYachtModel()"
            @change:country="setCountry"
          />
        </v-fade-transition>
      </v-col>
    </v-row>

    <v-navigation-drawer
      app
      permanent
      right
      width="400px"
      class="text-body-1"
      style="height: 100%"
    >
      <div
        class="d-flex flex-column no-gutters fill-height"
      >
        <div class="col col-auto">
          <div class="subtitle-1 grey--text text--darken-2">
            Annex1 Configuration
          </div>

          <div class="grey--text text-body-2 mb-6">
            Save changes to view updated annex 1.
          </div>
          <v-tabs
            v-model="pricingCountryId"
            class="ma-0"
            background-color="transparent"
          >
            <v-tab
              v-for="country in yachtModel.data.countries"
              :key="country.id"
              :href="'#' + country.id"
              class="text-none"
              style="min-width: 36px; padding: 0 14px"
            >
              <flag
                :iso="country.code"
                :squared="false"
                style="font-size: 10px"
                class="mr-1"
              />
            </v-tab>
          </v-tabs>
          <v-divider />
        </div>
        <div class="col overflow-auto pt-6 pb-6">
          <v-card flat>
            <v-card-text class="pa-0">
              <small-pricing-list
                :base-price="yachtModel.data.sale_price"
                :base-currency="yachtModel.data.currency"
                :equipment-price="countryData.equipment_sale_price"
                :equipment-packs="equipmentPacksByCountry"
                :cdr-cost="countryData.cost_cdr"
                :currency="yachtModel.data.currency"
                :currency-exchange-year="yachtModel.data.model_year"
                :total-price="countryData.total_sale_price"
                :model-year="yachtModel.data.model_year"
                :production-year="yachtModel.data.model_year"
              />
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-navigation-drawer>

    <yacht-model-edit-dialog
      :visible.sync="yachtModelEditDialog"
      :yacht-model="yachtModel.data"
      @yachtModel:created="fetchYachtModel()"
      @yachtModel:updated="fetchYachtModel()"
      @yachtModel:deleted="$router.push({ name: 'yachtModels' })"
    />
  </v-container>
</template>

<script>
import YachtModel from '@/models/YachtModel'
import SmallPricingList from '@/components/business/yachts/SmallPricingList.vue'
import { sortBy } from 'lodash'
import YachtModelEditDialog from '@/components/settings/yachtModels/YachtModelEditDialog.vue'

export default {
  name: 'YachtModelDetails',
  components: { YachtModelEditDialog, SmallPricingList },
  data() {
    return {
      yachtModel: {
        data: null,
        loading: false
      },
      pricingCountryId: null,
      yachtModelEditDialog: false
    }
  },
  computed: {
    countryData() {
      if (!this.pricingCountryId) return {}

      const country = this.yachtModel.data.countries.find(c => c.id === parseInt(this.pricingCountryId))

      return country ? country.pivot : {}
    },
    equipmentPacksByCountry() {
      return sortBy(this.yachtModel.data.equipment_packs, 'category.order').filter(ep => {
        if (!ep.countries.some(c => c.id === parseInt(this.pricingCountryId))) return false

        return true
      }).filter(ep => ep.default)
    }
  },
  created() {
    this.fetchYachtModel()

    this.yachtModelStatusData = YachtModel.statusData
  },
  methods: {
    async fetchYachtModel() {
      this.yachtModel.loading = true

      try {
        this.yachtModel.data = await YachtModel.api.find(this.$route.params.id)
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
      } finally {
        this.yachtModel.loading = false
      }
    },
    setCountry(countryId) {
      this.pricingCountryId = countryId.toString()
    }
  }
}

</script>
